import { get, post, put, del, patch } from "@/shared/utils/fetchApi";

export interface BilingualString {
  en: string;
  pt: string;
}

export interface PlanFeatureItem {
  en: string;
  pt: string;
}

export interface CreditUnitCost {
  cost: number;
  label: BilingualString;
}

export interface FixedLimit {
  max: number | null;
  label: BilingualString;
  unlimited?: boolean;
}

export interface PlanMetadata {
  badge?: BilingualString;
  color?: string;
  popular?: boolean;
  highlight?: boolean;
  cta_text?: BilingualString;
  restrictions?: PlanFeatureItem[];
  [key: string]: unknown;
}

export interface PlanContent {
  name?: string;
  description?: BilingualString;
  price_usd?: number;
  price_brl?: number;
  billing_cycle?: "monthly" | "semi-annual" | "annual" | "lifetime";
  trial_days?: number;
  invoice?: 0 | 1;
  features?: PlanFeatureItem[];
  metadata?: PlanMetadata;
  credits?: {
    limit?: number | null;
    label?: BilingualString;
    unit_costs?: Record<string, CreditUnitCost>;
  };
  fixed_limits?: Record<string, FixedLimit>;
  permission_list?: string | null;
  integrations?: string | null;
}

export interface Plan {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  sort_order?: number;
  content: PlanContent;
  created_at?: string;
  updated_at?: string;
}

export interface CreatePlanInput {
  name: string;
  slug: string;
  content: PlanContent;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdatePlanInput {
  name?: string;
  slug?: string;
  content?: PlanContent;
  sort_order?: number;
  is_active?: boolean;
}

export class PlansAdminService {
  static async getPlans(): Promise<Plan[]> {
    // `GET /plans?include_inactive=true` (superadmin): array flat com TODOS os
    // planos, sem envelope.
    return (await get<Plan[]>("/plans?include_inactive=true")) ?? [];
  }

  static async getPlan(id: string): Promise<Plan> {
    return get<Plan>(`/plans/${id}`);
  }

  static async createPlan(data: CreatePlanInput): Promise<Plan> {
    return post<Plan>("/plans", data);
  }

  static async updatePlan(id: string, data: UpdatePlanInput): Promise<Plan> {
    return put<Plan>(`/plans/${id}`, data);
  }

  static async togglePlan(id: string): Promise<Plan> {
    return patch<Plan>(`/plans/${id}/toggle`);
  }

  static async deletePlan(id: string): Promise<void> {
    // Responde 204 No Content.
    return del<void>(`/plans/${id}`);
  }
}
